import type {
  MRT_Cell,
  MRT_Column,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
} from '@brightdrop/table-ui';
import type { User } from '@gm-commercial/profile-model';
import type { HTMLAttributeAnchorTarget, ReactNode, RefObject } from 'react';

import LocalizedPhoneNumber from '~/common/components/localization/LocalizedPhoneNumber';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  EmailTextRenderer,
  entityNameRenderer,
  getLinkRenderer,
} from '~/common/components/table/renderers/columnRenderer';
import {
  getFormattedPhone,
  getFullAddress,
} from '~/common/components/table/utils/table.utils';
import type { RouteParams } from '~/common/configs/route.config';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import type { ActivationStatus } from '~/common/models/common.model';
import { type RouteConfig, RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';

import UserFleetColumnCell from './cells/UserFleetColumnCell';
import UserInvitationStatusColumnCell from './cells/UserInvitationStatusColumnCell';
import UserLastSignedInColumnCell from './cells/UserLastSignedInColumnCell';
import UserNameColumnCell from './cells/UserNameColumnCell';
import UserOrgColumnCell from './cells/UserOrgColumnCell';
import UserRoleColumnCell from './cells/UserRoleColumnCell';

type UserListParams = Pick<RouteParams, 'organizationsId' | 'fleetsId'>;

export const DEFAULT_COLUMN_SIZE = 100;

export const USER_LIST_ADAPTERS = {
  organization: entityNameRenderer,
  address: getFullAddress,
  fleet: entityNameRenderer,
  phoneNumber: getFormattedPhone,
};

export const getMRTRouteLinkRenderer = <
  ValueType extends string,
  RowType extends Record<string, unknown>,
>({
  route,
  exact = false,
  valueAdapter = (value: ValueType) => `${value}`,
  paramAdapter = () => ({}),
  alwaysLink = false,
  disableRouting = false,
  target,
  withQueryParams = false,
}: {
  route: string | RouteConfig;
  exact?: boolean;
  valueAdapter?: (value: ValueType) => string;
  paramAdapter?: (value: ValueType, tableMeta: RowType) => RouteParams;
  alwaysLink?: boolean;
  disableRouting?: boolean;
  target?: HTMLAttributeAnchorTarget;
  withQueryParams?: boolean;
}): ((props: {
  cell: MRT_Cell<RowType, ValueType>;
  column: MRT_Column<RowType, ValueType>;
  renderedCellValue: ReactNode;
  row: MRT_Row<RowType>;
  rowRef?: RefObject<HTMLTableRowElement | null>;
  staticColumnIndex?: number;
  staticRowIndex?: number;
  table: MRT_TableInstance<RowType>;
}) => ReactNode) => {
  const RouteLinkRenderer = (props: {
    cell: MRT_Cell<RowType, ValueType>;
    column: MRT_Column<RowType, ValueType>;
    renderedCellValue: ReactNode;
    row: MRT_Row<RowType>;
    rowRef?: RefObject<HTMLTableRowElement | null>;
    staticColumnIndex?: number;
    staticRowIndex?: number;
    table: MRT_TableInstance<RowType>;
  }): JSX.Element => {
    const displayValue = valueAdapter(props.cell.getValue());
    const routeParams: RouteParams = paramAdapter(
      props.cell.getValue(),
      props.row.original
    );

    return getLinkRenderer({
      displayValue,
      to: !disableRouting
        ? makeRoutePath(route, routeParams, exact, withQueryParams)
        : undefined,
      alwaysLink,
      target,
    });
  };
  return RouteLinkRenderer;
};

const NAME_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'displayName',
  header: translateText('user:name'),
  minSize: 240,
  maxSize: 240,
  size: 240,
  Cell: UserNameColumnCell,
});

const ACCOUNT_LIST_NAME_COLUMN = (
  isActionsFlagEnabled: boolean
): MRT_ColumnDef<User, string> => ({
  ...NAME_COLUMN(),
  Cell: ({ cell, row }) =>
    UserNameColumnCell({
      cell,
      row,
      showUserDetailLink: false,
      showEmail: isActionsFlagEnabled,
    }),
});

const ROLE_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'role',
  header: translateText('user:role'),
  Cell: ({ cell }) => translateText(`common:roleList.${cell.getValue()}`),
});

const ACCOUNT_LIST_ROLE_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'role',
  header: translateText('user:role'),
  Cell: UserRoleColumnCell,
});

const PHONE_NUMBER_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'phoneNumber',
  header: translateText('user:phone'),
  Cell: ({ cell }) => <LocalizedPhoneNumber phone={cell.getValue()} />,
});

const EMAIL_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'email',
  header: translateText('common:email'),
  Cell: ({ cell }) => EmailTextRenderer(cell.getValue()),
});

const ADDRESS_COLUMN = (): MRT_ColumnDef<User, string> => ({
  id: 'address',
  accessorFn: (row) =>
    row?.address ? getFullAddress(row.address) : DEFAULT_EMPTY_VALUE,
  header: translateText('user:address'),
});

const STATUS_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'status',
  header: translateText('common:status'),
  Cell: ({ cell }) =>
    translateText(
      `common:propertyState.${cell.getValue() as ActivationStatus}`
    ),
});

const ORG_COLUMN = (): MRT_ColumnDef<User, string> => ({
  header: translateText('user:organization'),
  accessorKey: 'organization',
  minSize: 240,
  maxSize: 240,
  size: 240,
  Cell: UserOrgColumnCell,
});

const FLEET_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorKey: 'fleet',
  header: translateText('common:fleet'),
  Cell: UserFleetColumnCell,
});

const LAST_SIGNED_IN_COLUMN = (): MRT_ColumnDef<User, string> => ({
  accessorFn: (row) => row?.lastSignedInDate ?? DEFAULT_EMPTY_VALUE,
  accessorKey: 'lastSignedInDate',
  header: translateText('user:lastSignedIn'),
  size: 110,
  maxSize: 110,
  Cell: UserLastSignedInColumnCell,
});

const INVITATION_STATUS_COLUMN = (): MRT_ColumnDef<User, string> => ({
  //if users have an undefined status for their invite, then it means it's a legacy account or has been whitelisted
  accessorFn: (row) => row?.invite?.status || DEFAULT_EMPTY_VALUE,
  accessorKey: 'invite.status',
  header: translateText('user:invitationStatus'),
  size: 110,
  maxSize: 110,
  Cell: UserInvitationStatusColumnCell,
});

// User List for ALL Organizations
const ALL_ORGS_COLUMNS = (): MRT_ColumnDef<User, string>[] => [
  NAME_COLUMN(),
  ROLE_COLUMN(),
  ORG_COLUMN(),
  EMAIL_COLUMN(),
  PHONE_NUMBER_COLUMN(),
  ADDRESS_COLUMN(),
  STATUS_COLUMN(),
];

const SINGLE_ORG_USER_LIST_COLUMNS = (
  isActionsEnabled: boolean
): MRT_ColumnDef<User, string>[] => [
  ...(isActionsEnabled ? [] : [EMAIL_COLUMN()]),
  ACCOUNT_LIST_NAME_COLUMN(isActionsEnabled),
  ...(isActionsEnabled ? [ACCOUNT_LIST_ROLE_COLUMN()] : [ROLE_COLUMN()]),
  FLEET_COLUMN(),
  INVITATION_STATUS_COLUMN(),
  LAST_SIGNED_IN_COLUMN(),
];

export const getUserListColumnConfig = (
  params: UserListParams
): MRT_ColumnDef<User, string>[] => {
  /* 3 current routing scenarios to display the different User List View
  1 - No Org Id => All Organizations
  2 - Org Id => Specific Organization
  3 - Org Id to Fleet Id => Specific Fleet under Org
  */
  const { organizationsId } = params;
  return organizationsId
    ? SINGLE_ORG_USER_LIST_COLUMNS(false)
    : ALL_ORGS_COLUMNS();
};

export const getUserManagementListColumnConfig = (
  isActionsEnabled = false
): MRT_ColumnDef<User, string>[] => {
  return SINGLE_ORG_USER_LIST_COLUMNS(isActionsEnabled);
};

export const getOrganizationUserColumnDefinitions = (): MRT_ColumnDef<
  User,
  string
>[] => [
  NAME_COLUMN(),
  ROLE_COLUMN(),
  ORG_COLUMN(),
  FLEET_COLUMN(),
  INVITATION_STATUS_COLUMN(),
  LAST_SIGNED_IN_COLUMN(),
];
export const getGMUserColumnDefinitions = (): MRT_ColumnDef<User, string>[] => [
  NAME_COLUMN(),
  ROLE_COLUMN(),
  LAST_SIGNED_IN_COLUMN(),
];
